import { QuestionAnswerMap } from '@care-portals/api-interfaces';

const enclomipheneFollowupQuestions = [
  {
    field: 'enclomipheneFollowup_sexAssignedAtBirth',
    label: 'What was your sex assigned at birth?',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    }
  },
  {
    field: 'enclomipheneFollowup_height',
    label: 'What is your height in feet and inches?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 1,
      name: 'enclomipheneFollowup_height'
    }
  },
  {
    field: 'enclomipheneFollowup_weight',
    label: 'What is your weight in pounds?',
    type: 'info',
    answer: {
      type: 'number',
      name: 'enclomipheneFollowup_weight'
    }
  },
  {
    field: 'enclomipheneFollowup_healthChanges',
    label:
      'Have there been any health changes, medication changes, ED visits or hospitalizations since your last check-in?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'enclomipheneFollowup_healthChangesDetails',
    label: 'Please tell us more about the changes in your health',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'enclomipheneFollowup_healthChangesDetails'
    }
  },
  {
    field: 'enclomipheneFollowup_medicationDoing',
    label: 'How are you doing on the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Well', key: 'well' },
        { value: 'Not well', key: 'not_well' }
      ]
    }
  },
  {
    field: 'enclomipheneFollowup_medicationExperience',
    label: 'Please tell us more about what you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'enclomipheneFollowup_medicationExperience'
    }
  },
  {
    field: 'enclomipheneFollowup_sideEffects',
    label: 'Have you had any side effects from the current medication?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'No', key: 'no' },
        { value: 'Yes', key: 'yes' }
      ]
    }
  },
  {
    field: 'enclomipheneFollowup_sideEffectsDetails',
    label: 'Please tell us more about the side effects you are experiencing',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'enclomipheneFollowup_sideEffectsDetails'
    }
  },
  {
    field: 'enclomipheneFollowup_continueTreatment',
    label: 'How would you like to continue your treatment?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Stay at the same dose or equivalent dose', key: 'stay_at_same_dose' },
        { value: 'Increase the dose if a higher one is available, or continue with my current dose if it\'s already at the maximum', key: 'increase_dose' },
        { value: 'Decrease dose', key: 'decrease_dose' }

      ]
    }
  },
  {
    field: 'enclomipheneFollowup_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'enclomipheneFollowup_additionalQuestions'
    }
  },
  {
    field: 'enclomipheneFollowup_consentTreatment',
    label: 'Consent (Enclomiphene Treatment):',
    type: 'select',
    educationalSlide: `<div class="!whitespace-normal">
<p>Enclomiphene is a medication that may be prescribed to increase testosterone levels in men experiencing symptoms of low testosterone. While it is FDA-approved for other uses, its application for treating low testosterone in men is considered off-label, meaning it is not specifically approved by the FDA for this purpose, though your healthcare provider may prescribe it when deemed appropriate. The purpose of this consent form is to ensure that you understand the reasons for using enclomiphene, the potential benefits and risks, the monitoring plan, and your responsibilities during treatment. Please read this document carefully.</p>

<p>You have been diagnosed with symptoms suggestive of hypogonadism, such as fatigue, decreased libido, erectile dysfunction, mood changes, and reduced muscle mass. You may have low testosterone levels or testosterone levels within the normal range but with consistent symptoms, which is suggestive of functional hypogonadism. Enclomiphene is being considered as a treatment option to potentially improve your symptoms by stimulating your body's natural production of testosterone. As previously mentioned, the use of enclomiphene to increase testosterone levels in men is off-label. Although this medication is legally prescribed, it is being used in a way that differs from its original FDA-approved indications.</p>

<h2>Potential Benefits of Enclomiphene</h2>
<p>The potential benefits of enclomiphene include:</p>
<ul>
  <li>Increased testosterone levels</li>
  <li>Improved energy and mood</li>
  <li>Enhanced libido and sexual function</li>
  <li>Increased muscle mass and strength</li>
  <li>Potential improvement in fertility since enclomiphene does not suppress natural testosterone production</li>
</ul>

<h2>Potential Risks and Side Effects</h2>
<p>Potential risks and side effects include:</p>
<p>Common risks:</p>
<ul>
  <li>Increased risk of blood clots</li>
  <li>Mood changes</li>
  <li>Gynecomastia</li>
  <li>Elevated red blood cell count</li>
  <li>Headaches, nausea, and visual disturbances</li>
  <li>Prostate enlargement</li>
  <li>Worsening of pre-existing sleep apnea</li>
</ul>

<p>Serious but less common risks:</p>
<ul>
  <li>Exacerbation of undiagnosed prostate cancer</li>
  <li>Severe cardiovascular events, such as heart attack or stroke</li>
  <li>Liver toxicity</li>
</ul>

<h2>Monitoring Plan</h2>
<p>To ensure safety and effectiveness, regular monitoring is required. This includes:</p>
<ul>
  <li>Initial baseline testing to measure testosterone levels, liver function, and estradiol levels</li>
  <li>Ongoing monitoring with blood tests to assess treatment efficacy and safety</li>
  <li>Regular follow-up visits to discuss progress, side effects, and adjustments to your treatment plan</li>
</ul>

<h2>Alternative Treatments</h2>
<p>Alternative treatments include:</p>
<ul>
  <li>Traditional testosterone replacement therapy (injections, gels, or patches)</li>
  <li>Lifestyle modifications (exercise, diet, and stress management)</li>
  <li>Other medications to improve testosterone levels</li>
</ul>

<h2>Patient Responsibilities</h2>
<p>As a patient, you are responsible for:</p>
<ul>
  <li>Attending all scheduled follow-up appointments</li>
  <li>Reporting any side effects or new symptoms to your healthcare provider</li>
  <li>Adhering to the prescribed dosing regimen</li>
  <li>Undergoing all required laboratory tests and examinations as part of the monitoring plan</li>
</ul>

<p>By selecting below, you understand the potential risks and benefits of using enclomiphene and the off-label nature of this treatment. You agree to follow the monitoring plan and inform your healthcare provider of any side effects or concerns.</p>
</div>`,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read and understand the information and I DO NOT wish to proceed',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value: 'I have read and understand the information and wish to proceed',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'enclomipheneFollowup_complete',
    isLast: true,
    label:
      'Thank you for completing this questionnaire, please select "Finish" to submit',
    extras: { sendToBeluga: false },
    type: 'info',
    answer: {}
  },
  {
    class: 'ineligible',
    field: 'enclomipheneFollowup_ineligible',
    label: "We're sorry you don't qualify",
    subLabel: "We're not able to prescribe for you today",
    extras: { sendToBeluga: false },
    answer: {},
    type: 'info',
    isLast: true
  }
] as const;

export type EnclomipheneFollowupFieldAnswers = Partial<
  QuestionAnswerMap<typeof enclomipheneFollowupQuestions>
>;

export default enclomipheneFollowupQuestions;
